/* eslint-disable no-unused-vars */
import { round } from 'lodash'
import co2RateArray from 'src/data/french/apfp/co2Rate.json'
import type {
	ReduxQuote,
	ReduxQuoteInfo,
	ReduxRecapInfo,
} from 'src/types/apiType'
import type { QuoteType } from 'src/types/miscType'
import type { ArrayTotals } from 'src/types/quoteTotalsTypes'
import { buildTaxDetailsWithAllocatedBases } from 'src/utils/quote/quoteTotalsSummary/taxAllocationUtils'
import {
	categories,
	cnc5chCategories,
	quote360TemplateCategories,
	quoteAnimationAndLiveActionCategories,
	quoteAnimationOnlyCategories,
	quoteDigitalCategories,
} from 'src/utils/quoteGridUtils/createQuoteUtil'

const co2Rate = co2RateArray.reduce<Record<string, number>>((acc, item) => {
	if (item.subCategoryUuid && item.co2coeff) {
		acc[item.subCategoryUuid] = round(item.co2coeff, 2)
	}
	if (item.subCategoryUuid_international && item.co2coeff) {
		acc[item.subCategoryUuid_international] = round(item.co2coeff, 2)
	}
	return acc
}, {})

function getQuoteCategories(quoteType?: QuoteType) {
	switch (quoteType) {
		case 'frenchApfp':
			return categories
		case 'frenchPostProd':
			return categories
		case 'cnc5ch':
			return cnc5chCategories
		case 'quoteAnimationOnly':
			return quoteAnimationOnlyCategories
		case 'quoteAnimationAndLiveAction':
			return quoteAnimationAndLiveActionCategories
		case 'quoteDigital':
			return quoteDigitalCategories
		case '360Template':
			return quote360TemplateCategories
		default:
			return categories
	}
}

function quoteTotalsSummary(
	quoteDetails: ReduxQuote,
	quoteInfo: ReduxQuoteInfo[],
	recapInfo: ReduxRecapInfo[] = []
) {
	// create a totals object for the quote
	const totalsObject = {
		id: null,
		name: null,
		categoryTotals: [],
		franceCategoryTotals: [],
		internationalCategoryTotals: [],
		totals: {},
		franceTotals: {},
		internationalTotals: {},
		discount: null,
		totalsWithTax: {},
		subtotalsWithAccSections: [],
		subtotalsWithQuoteType: [],
		totalCo2EmissionDetails: [],
	} as any
	// if quote doesn't exist it sends back the empty totalsObject
	if (!quoteDetails) {
		return totalsObject
	}
	// set name and id of quote on totals object
	totalsObject.id = quoteDetails.id ?? ''
	totalsObject.name = quoteDetails.name ?? ''
	// initialize the quote totals object
	const totals = getTotalTemplateObject()
	const franceTotals = getTotalTemplateObject()
	const internationalTotals = getTotalTemplateObject()
	// get the category totals template array for all categories
	const categoryTotalsArray = getCategoryTotalsTemplateArray(
		quoteDetails,
		quoteInfo,
		recapInfo,
		null
	)
	const franceCategoryTotalsArray = getCategoryTotalsTemplateArray(
		quoteDetails,
		quoteInfo,
		recapInfo,
		'f'
	)
	const internationalCategoryTotalsArray = getCategoryTotalsTemplateArray(
		quoteDetails,
		quoteInfo,
		recapInfo,
		'i'
	)
	const allTotalsObject = {
		totals,
		franceTotals,
		internationalTotals,
		categoryTotalsArray,
		franceCategoryTotalsArray,
		internationalCategoryTotalsArray,
	}
	//We first need to calculate the payroll for each costline and create a dict of categoryUuid and payroll tax for that category
	const payrollTaxRateMap: { [key: string]: number | null | undefined } = {}
	for (const costline of quoteInfo) {
		if (
			costline.costLineType === 'costLine' &&
			costline.category === 'Payroll Taxes' &&
			costline.item
		) {
			payrollTaxRateMap[costline.item] = costline.quantity
		}
	}

	const payrollTaxRateWithMarginAndOverheadMap: {
		[key: string]: number | null | undefined
	} = {}
	for (const costline of quoteInfo) {
		if (
			costline.costLineType === 'costLine' &&
			costline.category === 'Payroll Taxes' &&
			costline.item &&
			costline.total &&
			costline.rateInEuros
		) {
			payrollTaxRateWithMarginAndOverheadMap[costline.item] = round(
				(costline.total + (costline.totalMargin || 0)) /
					costline.rateInEuros,
				5
			)
		} else if (
			costline.costLineType === 'costLine' &&
			costline.category === 'Payroll Taxes' &&
			costline.item
		) {
			payrollTaxRateWithMarginAndOverheadMap[costline.item] = 0
		}
	}

	const payrollTaxTotalPerCategory: { [key: string]: number } = {}
	for (const costline of quoteInfo) {
		if (
			costline.categoryUuid &&
			!payrollTaxTotalPerCategory[costline.categoryUuid]
		) {
			payrollTaxTotalPerCategory[costline.categoryUuid] = 0
		}
		if (
			costline.costLineType === 'costLine' &&
			costline.payrollTaxType &&
			costline?.total &&
			costline?.total > 0 &&
			costline.categoryUuid
		) {
			const payrollTaxTotal =
				costline.total *
				(payrollTaxRateMap[costline.payrollTaxType] || 0)
			payrollTaxTotalPerCategory[costline.categoryUuid] += payrollTaxTotal
		}
	}
	const payrollTaxTotalPerCategoryWithMarginAndOverhead: {
		[key: string]: number
	} = {}
	for (const costline of quoteInfo) {
		if (
			costline.categoryUuid &&
			!payrollTaxTotalPerCategoryWithMarginAndOverhead[
				costline.categoryUuid
			]
		) {
			payrollTaxTotalPerCategoryWithMarginAndOverhead[
				costline.categoryUuid
			] = 0
		}
		if (
			costline.costLineType === 'costLine' &&
			costline.payrollTaxType &&
			costline?.total &&
			costline?.total > 0 &&
			costline.categoryUuid
		) {
			const payrollTaxTotal =
				costline.total *
				(payrollTaxRateWithMarginAndOverheadMap[
					costline.payrollTaxType
				] || 0)
			payrollTaxTotalPerCategoryWithMarginAndOverhead[
				costline.categoryUuid
			] += payrollTaxTotal
		}
	}

	// Add diagnostic logging for payroll tax calculation
	if (quoteDetails?.marginCustomization === 'noPayrollAndMargin') {
		console.log(
			'[frenchApfpAndPostProdQuoteTotalsSummary] Payroll tax totals per category:',
			payrollTaxTotalPerCategoryWithMarginAndOverhead
		)
	}

	// get all the category total rows from quote Info, so at this point we have the latest budget total information
	// also initialize the provisionalTotal and actualTotal to be 0 for all these categories.
	let categoryTotalRows = quoteInfo
		.filter((row) => row.costLineType === 'categoryTotal')
		.map((totalRow) => {
			const localTotalRow = { ...totalRow } as ReduxQuoteInfo & {
				provisionalTotal: number
				actualTotal: number
			}
			localTotalRow.provisionalTotal = 0
			localTotalRow.actualTotal = 0
			return localTotalRow
		})

	const categoryCo2EmissionRows = quoteInfo.reduce<{ [key: string]: number }>(
		(acc, row) => {
			if (
				row &&
				row.costLineType === 'subCategoryTotal' &&
				row.categoryUuid
			) {
				const totalCo2Emission = getCo2Emission(row, quoteDetails)
				acc[row.categoryUuid] = round(
					(acc[row.categoryUuid] || 0) + totalCo2Emission,
					4
				)
			}
			return acc
		},
		{}
	)

	// if grid2 / recapInfo has been populated, we might have differet provisional and actual total information
	if (recapInfo?.length) {
		// get all the category total rows in recap info (essentially all categories that have totals)
		const recapInfoCategoryTotalRows = recapInfo.filter(
			(row) => row.costLineType === 'categoryTotal'
		)
		// Sets the the category totals for provisional and actual we got from above to the categoryTotalRows
		categoryTotalRows = categoryTotalRows.map((totalRow) => {
			const localTotalRow = { ...totalRow }
			// Finds the index in recapInfoCategory Totals where the current category total name matches with recapCategoryTotal
			const recapInfoCategoryTotalRowIndex =
				recapInfoCategoryTotalRows.findIndex(
					(recapInfoTotalRow) =>
						totalRow.category === recapInfoTotalRow.category
				)
			// If index was found we set the current categories provisional and actual total to that of recapInfo
			if (recapInfoCategoryTotalRowIndex >= 0) {
				const { provisionalTotal, actualTotal } =
					recapInfoCategoryTotalRows[recapInfoCategoryTotalRowIndex]
				localTotalRow.provisionalTotal = provisionalTotal ?? 0
				localTotalRow.actualTotal = actualTotal ?? 0
			}
			return localTotalRow
		})
	}

	// loop over the categoryTotalRows -
	// 1. update the categoryTotalsArray (templateArray) with correct total values
	// 2. update the totalsObjects (categoryTotalsArray, totals, franceTotals, internationalTotals) of the quote with the correct totals
	let finalAllTotalsObject = categoryTotalRows.reduce(
		(allTotalsObject, totalRow, index) => {
			// allTotalsObject is defined above with all relevant details
			// Finds the index of the category index where
			const categoryTotalsArrayIndex =
				allTotalsObject.categoryTotalsArray.findIndex(
					(row) =>
						row.category.toLowerCase().trim() ===
						totalRow?.category
							?.replace(' - EXE', '')
							.toLowerCase()
							.trim()
				)
			if (!totalRow.isInternational) {
				// if it is a france category
				const franceCategoryTotalsArrayIndex =
					allTotalsObject.franceCategoryTotalsArray.findIndex(
						(row) =>
							row.category ===
							totalRow?.category?.replace(' - EXE', '')
					)
				// If the catergoryTotalsArray has the index for the totals we save the totals value in allTotalsObject
				// provisional & actualTotal are set to 0 if not present
				if (categoryTotalsArrayIndex >= 0) {
					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].budgetTotal = round(totalRow?.total || 0, 2)
					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].budgetWithMarginTotal = round(
						(totalRow.total || 0) + (totalRow.totalMargin || 0),
						2
					)
					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].budgetWithPayrollTaxTotal = round(
						(totalRow.total || 0) +
							payrollTaxTotalPerCategory[totalRow.categoryUuid!],
						2
					)
					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].budgetWithPayrollAndMarginTotal = round(
						(totalRow.total || 0) +
							(totalRow.totalMargin || 0) +
							payrollTaxTotalPerCategoryWithMarginAndOverhead[
								totalRow.categoryUuid!
							],
						2
					)
					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].provisionalTotal = totalRow.provisionalTotal
					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].actualTotal = totalRow.actualTotal
				}
				if (franceCategoryTotalsArrayIndex >= 0) {
					allTotalsObject.franceCategoryTotalsArray[
						franceCategoryTotalsArrayIndex
					].budgetTotal = totalRow.total ?? 0

					allTotalsObject.franceCategoryTotalsArray[
						franceCategoryTotalsArrayIndex
					].budgetWithMarginTotal = round(
						(totalRow.total || 0) + (totalRow.totalMargin || 0),
						2
					)
					allTotalsObject.franceCategoryTotalsArray[
						franceCategoryTotalsArrayIndex
					].budgetWithPayrollTaxTotal = round(
						(totalRow.total || 0) +
							payrollTaxTotalPerCategory[totalRow.categoryUuid!],
						2
					)
					allTotalsObject.franceCategoryTotalsArray[
						franceCategoryTotalsArrayIndex
					].budgetWithPayrollAndMarginTotal = round(
						(totalRow.total || 0) +
							(totalRow.totalMargin || 0) +
							payrollTaxTotalPerCategoryWithMarginAndOverhead[
								totalRow.categoryUuid!
							],
						2
					)

					allTotalsObject.franceCategoryTotalsArray[
						franceCategoryTotalsArrayIndex
					].provisionalTotal = totalRow.provisionalTotal
					allTotalsObject.franceCategoryTotalsArray[
						franceCategoryTotalsArrayIndex
					].actualTotal = totalRow.actualTotal
				}
			} else {
				// if it is an international category
				const internationalCategoryTotalsArrayIndex =
					allTotalsObject.internationalCategoryTotalsArray.findIndex(
						(row) =>
							row.category ===
							totalRow?.category?.replace(' - EXE', '')
					)
				// for the normal totals array, add the international totals to the existing france totals
				if (categoryTotalsArrayIndex >= 0) {
					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].budgetTotal += round(totalRow.total || 0, 2)

					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].budgetWithMarginTotal += round(
						(totalRow.total || 0) + (totalRow.totalMargin || 0),
						2
					)

					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].budgetWithPayrollTaxTotal = round(
						(totalRow.total || 0) +
							payrollTaxTotalPerCategory[totalRow.categoryUuid!],
						2
					)
					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].budgetWithPayrollAndMarginTotal = round(
						(totalRow.total || 0) +
							(totalRow.totalMargin || 0) +
							payrollTaxTotalPerCategoryWithMarginAndOverhead[
								totalRow.categoryUuid!
							],
						2
					)

					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].provisionalTotal += round(totalRow.provisionalTotal, 2)
					allTotalsObject.categoryTotalsArray[
						categoryTotalsArrayIndex
					].actualTotal += round(totalRow.actualTotal, 2)
				}
				if (internationalCategoryTotalsArrayIndex >= 0) {
					allTotalsObject.internationalCategoryTotalsArray[
						internationalCategoryTotalsArrayIndex
					].budgetTotal = round(totalRow.total || 0, 2)

					allTotalsObject.internationalCategoryTotalsArray[
						internationalCategoryTotalsArrayIndex
					].budgetWithMarginTotal += round(
						(totalRow.total || 0) + (totalRow.totalMargin || 0),
						2
					)
					allTotalsObject.internationalCategoryTotalsArray[
						internationalCategoryTotalsArrayIndex
					].budgetWithPayrollTaxTotal = round(
						(totalRow.total || 0) +
							payrollTaxTotalPerCategory[totalRow.categoryUuid!],
						2
					)
					allTotalsObject.internationalCategoryTotalsArray[
						internationalCategoryTotalsArrayIndex
					].budgetWithPayrollAndMarginTotal = round(
						(totalRow.total || 0) +
							(totalRow.totalMargin || 0) +
							payrollTaxTotalPerCategoryWithMarginAndOverhead[
								totalRow.categoryUuid!
							],
						2
					)

					allTotalsObject.internationalCategoryTotalsArray[
						internationalCategoryTotalsArrayIndex
					].provisionalTotal = round(totalRow.provisionalTotal, 2)

					allTotalsObject.internationalCategoryTotalsArray[
						internationalCategoryTotalsArrayIndex
					].actualTotal = round(totalRow.actualTotal, 2)
				}
			}

			if (
				categoryTotalsArrayIndex >= 0 &&
				totalRow.categoryUuid &&
				categoryCo2EmissionRows[totalRow.categoryUuid]
			) {
				// Add co2 emission to totals for basic co2 emission calculation

				const co2Emission =
					categoryCo2EmissionRows[totalRow.categoryUuid]

				allTotalsObject.categoryTotalsArray[
					categoryTotalsArrayIndex
				].totalCo2Emission += round(co2Emission, 2)
				allTotalsObject.totals.totalCo2Emission += round(co2Emission, 2)
			}

			if (totalRow.total) {
				allTotalsObject.totals.budgetSubTotal += totalRow.total
				allTotalsObject.totals.budgetGrandTotal += totalRow.total
				// actual and provisional grand total are always the same as bugetGrandTotal
				allTotalsObject.totals.actualGrandTotal += totalRow.total
				allTotalsObject.totals.provisionalGrandTotal += totalRow.total
				if (totalRow.isInternational) {
					allTotalsObject.internationalTotals.budgetSubTotal +=
						totalRow.total
					allTotalsObject.internationalTotals.budgetGrandTotal +=
						totalRow.total
					// actual and provisional grand total are always the same as bugetGrandTotal
					allTotalsObject.internationalTotals.actualGrandTotal +=
						totalRow.total
					allTotalsObject.internationalTotals.provisionalGrandTotal +=
						totalRow.total
				} else {
					allTotalsObject.franceTotals.budgetSubTotal +=
						totalRow.total
					allTotalsObject.franceTotals.budgetGrandTotal +=
						totalRow.total
					// actual and provisional grand total are always the same as bugetGrandTotal
					allTotalsObject.franceTotals.actualGrandTotal +=
						totalRow.total
					allTotalsObject.franceTotals.provisionalGrandTotal +=
						totalRow.total
				}
			}
			if (totalRow.actualTotal) {
				allTotalsObject.totals.actualSubTotal += totalRow.actualTotal
				if (totalRow.isInternational) {
					allTotalsObject.internationalTotals.actualSubTotal +=
						totalRow.actualTotal
				} else {
					allTotalsObject.franceTotals.actualSubTotal +=
						totalRow.actualTotal
				}
			}
			if (totalRow.provisionalTotal) {
				allTotalsObject.totals.provisionalSubTotal +=
					totalRow.provisionalTotal
				if (totalRow.isInternational) {
					allTotalsObject.internationalTotals.provisionalSubTotal +=
						totalRow.provisionalTotal
				} else {
					allTotalsObject.franceTotals.provisionalSubTotal +=
						totalRow.provisionalTotal
				}
			}
			if (totalRow.totalMargin) {
				allTotalsObject.totals.budgetMargin += totalRow.totalMargin
				allTotalsObject.totals.budgetGrandTotal += totalRow.totalMargin
				// add the budget margin to actual grand total only when actual grand total exists
				if (allTotalsObject.totals.actualGrandTotal)
					allTotalsObject.totals.actualGrandTotal +=
						totalRow.totalMargin
				// add the budget margin to provisional grand total only when provisional grand total exists
				if (allTotalsObject.totals.provisionalGrandTotal)
					allTotalsObject.totals.provisionalGrandTotal +=
						totalRow.totalMargin
				if (totalRow.isInternational) {
					allTotalsObject.internationalTotals.budgetMargin +=
						totalRow.totalMargin
					allTotalsObject.internationalTotals.budgetGrandTotal +=
						totalRow.totalMargin
					// add the budget margin to actual grand total only when actual grand total exists
					if (allTotalsObject.internationalTotals.actualGrandTotal)
						allTotalsObject.internationalTotals.actualGrandTotal +=
							totalRow.totalMargin
					// add the budget margin to provisional grand total only when provisional grand total exists
					if (
						allTotalsObject.internationalTotals
							.provisionalGrandTotal
					)
						allTotalsObject.internationalTotals.provisionalGrandTotal +=
							totalRow.totalMargin
				} else {
					allTotalsObject.franceTotals.budgetMargin +=
						totalRow.totalMargin
					allTotalsObject.franceTotals.budgetGrandTotal +=
						totalRow.totalMargin
					// add the budget margin to actual grand total only when actual grand total exists
					if (allTotalsObject.franceTotals.actualGrandTotal)
						allTotalsObject.franceTotals.actualGrandTotal +=
							totalRow.totalMargin
					// add the budget margin to provisional grand total only when provisional grand total exists
					if (allTotalsObject.franceTotals.provisionalGrandTotal)
						allTotalsObject.franceTotals.provisionalGrandTotal +=
							totalRow.totalMargin
				}
			}
			if (totalRow.total && totalRow.totalMargin) {
				allTotalsObject.totals.subTotalBudgetWithMargin +=
					totalRow.total
			}
			if (totalRow.total && !totalRow.totalMargin) {
				allTotalsObject.totals.subTotalBudgetWithoutMargin +=
					totalRow.total
			}
			return allTotalsObject
		},
		allTotalsObject
	) as any

	if (quoteDetails.quoteType === 'frenchApfp') {
		const {
			mealTotalCo2Emission,
			travelTotalCo2Emission,
			hotelTotalCo2Emission,
			location2TravelTotalCo2Emission,
		} = complexCo2Emission(quoteDetails)
		totalsObject.totalCo2EmissionDetails = [
			{ id: 'meal', total: mealTotalCo2Emission },
			{ id: 'travel', total: travelTotalCo2Emission },
			{ id: 'hotel', total: hotelTotalCo2Emission },
			{ id: 'location', total: location2TravelTotalCo2Emission },
			{ id: 'others', total: allTotalsObject.totals.totalCo2Emission },
		]

		allTotalsObject.totals.totalCo2Emission += mealTotalCo2Emission
		allTotalsObject.totals.totalCo2Emission += travelTotalCo2Emission
		allTotalsObject.totals.totalCo2Emission += hotelTotalCo2Emission
		allTotalsObject.totals.totalCo2Emission +=
			location2TravelTotalCo2Emission
		allTotalsObject.totals.totalCo2Emission = round(
			allTotalsObject.totals.totalCo2Emission || 0,
			2
		)
		allTotalsObject.categoryTotalsArray.map((ct) => {
			if (ct.category === 'Travel & Accommodation') {
				ct.totalCo2Emission += travelTotalCo2Emission
				ct.totalCo2Emission += hotelTotalCo2Emission
				ct.totalCo2Emission += location2TravelTotalCo2Emission
			}
			if (ct.category === 'Unit') {
				ct.totalCo2Emission += mealTotalCo2Emission
			}
			ct.totalCo2Emission = round(ct.totalCo2Emission || 0, 2)
			return ct
		})
	}

	finalAllTotalsObject = quoteInfo.reduce((allTotalsObject, row, index) => {
		if (
			row.total &&
			row.costLineType === 'costLine' &&
			(row.margin || row.overhead)
		) {
			if (row.margin)
				allTotalsObject.totals.budgetSeperateMargin += row.margin
			if (row.overhead)
				allTotalsObject.totals.budgetSeperateOverhead += row.overhead
		}
		return allTotalsObject
	}, finalAllTotalsObject)

	// update the category totals array
	totalsObject.categoryTotals = finalAllTotalsObject.categoryTotalsArray
	totalsObject.franceCategoryTotals =
		finalAllTotalsObject.franceCategoryTotalsArray
	totalsObject.internationalCategoryTotals =
		finalAllTotalsObject.internationalCategoryTotalsArray

	// biome-ignore lint/performance/noDelete: <>
	delete finalAllTotalsObject.categoryTotalsArray
	// biome-ignore lint/performance/noDelete: <>
	delete finalAllTotalsObject.franceCategoryTotalsArray
	// biome-ignore lint/performance/noDelete: <>
	delete finalAllTotalsObject.internationalCategoryTotalsArray

	// calculate all margins in the totalsObjects and then round of all totals to two decimal places
	for (const key in finalAllTotalsObject) {
		finalAllTotalsObject[key] = calculateMarginsAndReturnUpdatedTotals(
			finalAllTotalsObject[key]
		)
		for (const totalKey in finalAllTotalsObject[key]) {
			finalAllTotalsObject[key][totalKey] = round(
				finalAllTotalsObject[key][totalKey],
				2
			)
		}
	}

	// assign the final totals to the actual totalsObject
	totalsObject.totals = finalAllTotalsObject.totals
	totalsObject.franceTotals = finalAllTotalsObject.franceTotals
	totalsObject.internationalTotals = finalAllTotalsObject.internationalTotals

	if (quoteDetails.discount?.totalDiscount) {
		// get the discount object
		const quoteDiscountObject = { ...quoteDetails.discount }
		const discount = getDiscountTemplateObject()

		// total after discount and discount is the same in cases
		discount.totalAfterDiscount = round(
			quoteDiscountObject?.totalAfterDiscount ?? 0,
			2
		)
		discount.totalDiscount = round(
			totalsObject.totals.budgetGrandTotal - discount.totalAfterDiscount,
			2
		)
		// budget discount values
		discount.budgetTotalDiscountOnMargin = round(
			totalsObject.totals.budgetMargin - discount.totalDiscount,
			2
		)
		discount.budgetTotalDiscountOnMarginPercentage = round(
			(discount.budgetTotalDiscountOnMargin /
				discount.totalAfterDiscount) *
				100,
			2
		)
		discount.budgetTotalDiscountPercentage = round(
			totalsObject.totals.budgetMarginPercentage -
				discount.budgetTotalDiscountOnMarginPercentage,
			2
		)

		// provisional discount values
		discount.provisionalTotalDiscountOnMargin = round(
			totalsObject.totals.provisionalMargin - discount.totalDiscount,
			2
		)
		discount.provisionalTotalDiscountOnMarginPercentage = round(
			(discount.provisionalTotalDiscountOnMargin /
				discount.totalAfterDiscount) *
				100,
			2
		)
		discount.provisionalTotalDiscountPercentage = round(
			totalsObject.totals.provisionalMarginPercentage -
				discount.provisionalTotalDiscountOnMarginPercentage,
			2
		)

		// actual discount values
		discount.actualTotalDiscountOnMargin = round(
			totalsObject.totals.actualMargin - discount.totalDiscount,
			2
		)
		discount.actualTotalDiscountOnMarginPercentage = round(
			(discount.actualTotalDiscountOnMargin /
				discount.totalAfterDiscount) *
				100,
			2
		)
		discount.actualTotalDiscountPercentage = round(
			totalsObject.totals.actualMarginPercentage -
				discount.actualTotalDiscountOnMarginPercentage,
			2
		)

		totalsObject.discount = discount
	} else {
		const discount = getDiscountTemplateObject()
		// total after discount and discount is the same in cases
		discount.totalAfterDiscount = totalsObject.totals.budgetGrandTotal
		totalsObject.discount = discount
	}

	const sectionOptions = [
		'servicesProvided20',
		'publishingRights10',
		'masterRights20',
		'billOfRights10',
		'billOfRights20',
		'rightMusic',
		'copyRightAndReproduction',
		'servicesProvided10',
		'synchroTradingService',
		'tradingService',
	]
	let subtotalPerSectionArray = [] as any

	// //  ! Remove when we add sections to costlines in this quote type
	// subtotalPerSectionArray = [
	//   {
	//     type: quoteDetails?.quoteType,
	//     amount: totalsObject?.discount?.totalAfterDiscount,
	//   },
	// ]
	// ! Will change once these quote types have sections
	// For now null, so that we can display conditionally non null values
	if (quoteDetails?.quoteType === 'blankQuote') {
		const vatRates = [0.1, 0.2]
		const overallTotal = totalsObject?.totals?.budgetGrandTotal
		const subTotal = totalsObject?.discount?.totalAfterDiscount
		const costlinesWithVat = quoteInfo.filter((row) => row.VAT_rate)
		// For margins
		const marginTaxRate = quoteDetails?.marginVAT_rate
		const marginAmount = totalsObject.totals.budgetMargin
		const discount = totalsObject?.discount?.totalDiscount
		const { taxArray, totalTaxAmount } = buildTaxDetailsWithAllocatedBases({
			vatRates,
			overallTotal,
			subTotal,
			totalDiscount: discount,
			getTotalAmountForRate: (taxRate) => {
				const filteredCostlines = costlinesWithVat.filter(
					(row) => row.VAT_rate === taxRate
				)
				let totalAmount = filteredCostlines.reduce(
					(sum, currentItem) => {
						return sum + (currentItem.total || 0)
					},
					0
				)

				if (taxRate === marginTaxRate) {
					totalAmount += marginAmount
				}

				return totalAmount
			},
		})

		const totalsWithTaxObject = {
			subTotal,
			taxAmount: totalTaxAmount,
			taxDetails: taxArray,
		}

		totalsObject.totalsWithTax = totalsWithTaxObject

		const subtotalPerSectionArray = [] as number[]
		const subtotalPerSectionWithoutDiscountArray = []
		const weightedAverageArray = [] as number[]
		let countSubsectionMoreThanZero = 0
		for (let j = 0; j < sectionOptions.length; j++) {
			const filteredCostlines = quoteInfo.filter(
				(row) => row.Acc_section === sectionOptions[j]
			)

			const sectionName = sectionOptions[j]
			let totalSubtotal = 0
			if (filteredCostlines && filteredCostlines.length > 0) {
				totalSubtotal = filteredCostlines.reduce((sum, currentItem) => {
					return sum + (currentItem.total || 0)
				}, 0)
			}

			if (sectionName === quoteDetails?.marginAcc_section) {
				totalSubtotal += totalsObject.totals.budgetMargin
			}

			const weightedAverage =
				Math.round(
					(totalSubtotal / overallTotal + Number.EPSILON) * 100
				) / 100

			if (totalSubtotal > 0) {
				countSubsectionMoreThanZero++
				weightedAverageArray.push(weightedAverage)
			}
			// let discountAmount = Math.round((weightedAverage * discount + Number.EPSILON) * 100) / 100
			//
			// let totalAfterDiscount =
			//   Math.round((totalSubtotal - discountAmount + Number.EPSILON) * 100) / 100
			//
			// let subtotalPerSectionObject = {
			//   type: sectionName,
			//   amount: totalAfterDiscount,
			// }
			const subtotalPerSectionWithoutDiscount = {
				type: sectionName,
				amount: totalSubtotal,
			}
			subtotalPerSectionWithoutDiscountArray.push(
				subtotalPerSectionWithoutDiscount
			)
			//
			// subtotalPerSectionArray.push(subtotalPerSectionObject)
		}
		//
		// totalsObject.subtotalsWithAccSections = subtotalPerSectionArray

		// eslint-disable-next-line no-unused-vars
		const totalWeight = weightedAverageArray.reduce((a, b) => a + b, 0)

		// ! Only runs on the odd chance that weighted average does not add up to 1
		// if (totalWeight !== 1) {

		const newSubtotalPerSectionArray =
			subtotalPerSectionWithoutDiscountArray.map((s, i) => {
				let weightedAverage =
					Math.round(
						(s.amount / overallTotal + Number.EPSILON) * 100
					) / 100

				if (i === countSubsectionMoreThanZero - 1) {
					const removeLastWeightedAverage = weightedAverageArray.pop()
					// !pop removes last elemnt and modifies current array
					const total = weightedAverageArray.reduce(
						(a, b) => a + b,
						0
					)

					weightedAverage = 1 - total
				}
				const discountAmount =
					Math.round(
						(weightedAverage * discount + Number.EPSILON) * 100
					) / 100

				const totalAfterDiscount =
					Math.round(
						(s.amount - discountAmount + Number.EPSILON) * 100
					) / 100

				s.amount = totalAfterDiscount

				return s
			})

		totalsObject.subtotalsWithAccSections = newSubtotalPerSectionArray
		// }

		const subtotalPerQuoteTypeArray = null
		totalsObject.subtotalsWithQuoteType = subtotalPerQuoteTypeArray
	} else {
		// For other quote types
		const vatRates = [0.2]
		const totalTaxAmount =
			totalsObject?.discount?.totalAfterDiscount * vatRates[0]

		const rateObject = {
			taxRate: 0.2,
			taxPercentage: 20,
			taxAmount: totalTaxAmount,
			taxableAmount: totalsObject?.discount?.totalAfterDiscount,
		}

		const totalsWithTaxObject = {
			subTotal: totalsObject?.discount?.totalAfterDiscount,
			taxAmount: totalTaxAmount,
			taxDetails: [rateObject],
		}

		totalsObject.totalsWithTax = totalsWithTaxObject

		subtotalPerSectionArray = null
		totalsObject.subtotalsWithAccSections = subtotalPerSectionArray
		const subtotalPerQuoteTypeArray = [
			{
				type: quoteDetails?.quoteType,
				amount: totalsObject?.discount?.totalAfterDiscount,
			},
		]

		totalsObject.subtotalsWithQuoteType = subtotalPerQuoteTypeArray
	}

	return totalsObject
}

function calculateMarginsAndReturnUpdatedTotals(quoteTotals: {
	budgetGrandTotal: number
	budgetMarginPercentage: number
	budgetMargin: number
	budgetSeperateMarginPercentage: number
	budgetSeperateMargin: number
	budgetSeperateOverheadPercentage: number
	budgetSeperateOverhead: number
	budgetSubTotal: number
	actualSubTotal: number
	actualMargin: number
	actualMarginPercentage: number
	provisionalSubTotal: number
	provisionalMargin: number
	provisionalMarginPercentage: number
}) {
	if (quoteTotals.budgetGrandTotal) {
		quoteTotals.budgetMarginPercentage = round(
			(quoteTotals.budgetMargin / quoteTotals.budgetGrandTotal) * 100,
			2
		)
		quoteTotals.budgetSeperateMarginPercentage = round(
			(quoteTotals.budgetSeperateMargin / quoteTotals.budgetGrandTotal) *
				100,
			2
		)
		quoteTotals.budgetSeperateOverheadPercentage = round(
			(quoteTotals.budgetSeperateOverhead /
				quoteTotals.budgetGrandTotal) *
				100,
			2
		)
	}

	const diffBetweenBudgetAndActual =
		quoteTotals.budgetSubTotal - quoteTotals.actualSubTotal
	quoteTotals.actualMargin = round(
		quoteTotals.budgetMargin + diffBetweenBudgetAndActual,
		2
	)
	if (quoteTotals.budgetGrandTotal)
		quoteTotals.actualMarginPercentage = round(
			(quoteTotals.actualMargin / quoteTotals.budgetGrandTotal) * 100,
			2
		)
	const diffBetweenBudgetAndProvisional =
		quoteTotals.budgetSubTotal - quoteTotals.provisionalSubTotal
	quoteTotals.provisionalMargin = round(
		quoteTotals.budgetMargin + diffBetweenBudgetAndProvisional,
		2
	)
	if (quoteTotals.budgetGrandTotal)
		quoteTotals.provisionalMarginPercentage = round(
			(quoteTotals.provisionalMargin / quoteTotals.budgetGrandTotal) *
				100,
			2
		)
	return quoteTotals
}

function getDiscountTemplateObject() {
	return {
		totalAfterDiscount: 0,
		totalDiscount: 0,
		budgetTotalDiscountOnMargin: 0,
		budgetTotalDiscountOnMarginPercentage: 0,
		budgetTotalDiscountPercentage: 0,
		provisionalTotalDiscountOnMargin: 0,
		provisionalTotalDiscountOnMarginPercentage: 0,
		provisionalTotalDiscountPercentage: 0,
		actualTotalDiscountOnMargin: 0,
		actualTotalDiscountOnMarginPercentage: 0,
		actualTotalDiscountPercentage: 0,
	}
}

function getTotalTemplateObject() {
	return {
		budgetSubTotal: 0,
		budgetGrandTotal: 0,
		budgetMargin: 0,
		budgetMarginPercentage: 0,
		budgetSeperateMargin: 0,
		budgetSeperateMarginPercentage: 0,
		budgetSeperateOverhead: 0,
		budgetSeperateOverheadPercentage: 0,
		provisionalSubTotal: 0,
		provisionalGrandTotal: 0,
		provisionalMargin: 0,
		provisionalMarginPercentage: 0,
		actualSubTotal: 0,
		actualGrandTotal: 0,
		actualMargin: 0,
		actualMarginPercentage: 0,

		// No margin
		grandBudgetWithMarginTotal: 0,
		noMarginTotalPercentage: 0,
		// Detail Margin
		subTotalBudgetWithMargin: 0,
		subTotalBudgetWithoutMargin: 0,
		subTotalBudgetWithMarginPercentage: 0,
		// co2 emission
		totalCo2Emission: 0,
	}
}

function getCategoryTotalsTemplateArray(
	quoteDetails: ReduxQuote,
	quoteInfo: ReduxQuoteInfo[],
	recapInfo: ReduxRecapInfo[],
	franceOrInternational: string | null
) {
	let categories = getQuoteCategories(quoteDetails.quoteType)
	if (franceOrInternational) {
		if (franceOrInternational === 'i') {
			// get only the international categories when explicit asked for international categories
			categories = categories.filter((c) => c.isInternational)
		} else {
			// keep only the france categories as they represent all the unique categories
			categories = categories.filter((c) => !c.isInternational)
		}
	} else {
		// get only the france categories as they represent all the unique categories
		categories = categories.filter((c) => !c.isInternational)
	}
	// the name is normalized by replacing ' - EXE' with '' in the map below to make sure all the categories have the same name
	return categories.map((c) => {
		return {
			isInternational: franceOrInternational ? c.isInternational : null,
			categoryPrefix: c.categoryPrefix,
			categoryRoman: c?.categoryPrefix?.substring(
				0,
				c.categoryPrefix.length - 1
			),
			categoryId: c.categoryId,
			categoryUuid: c.categoryUuid,
			category: c.category.replace(' - EXE', ''),
			category_fr: c.category_fr.replace(' - EXE', ''),
			budgetTotal: 0,
			provisionalTotal: 0,
			actualTotal: 0,
			budgetWithMarginTotal: 0,
			budgetWithPayrollTaxTotal: 0,
			budgetWithPayrollAndMarginTotal: 0,
			totalCo2Emission: 0,
		}
	})
}

function getCo2Emission(row: ReduxQuoteInfo, quoteDetails: ReduxQuote) {
	if (row.subCategoryUuid && co2Rate[row.subCategoryUuid] && row.total) {
		return round((row.total * co2Rate[row.subCategoryUuid]) / 1000, 2)
	}
	return 0
}

function complexCo2Emission(quoteDetails: ReduxQuote) {
	const numberOfPrepDaysCo2 = quoteDetails.numberOfPrepDaysCo2 || 0
	const numberOfShootDaysCo2 = quoteDetails.numberOfShootDaysCo2 || 0
	const numberOfPeopleOnSetCo2 = quoteDetails.numberOfPeopleOnSetCo2 || 0
	const enableTravelCo2 = quoteDetails.enableTravelCo2
	const travelTypeCo2 = quoteDetails.travelTypeCo2
	const percentageOfCrewTravelCo2 =
		(quoteDetails.percentageOfCrewTravelCo2 || 0) / 100
	const travelTypeLocation2Co2 = quoteDetails.travelTypeLocation2Co2
	const percentageOfCrewTravelLocation2Co2 =
		(quoteDetails.percentageOfCrewTravelLocation2Co2 || 0) / 100

	const travelType = {
		train: 10,
		shortFlight: 120,
		mediumFlight: 300,
		longFlight: 600,
		longHaulFlight: 1000,
	}

	let mealTotalCo2Emission =
		numberOfPeopleOnSetCo2 *
		(numberOfPrepDaysCo2 + numberOfShootDaysCo2) *
		(0.8 * 5 + 0.2 * 2.5)
	mealTotalCo2Emission = round(mealTotalCo2Emission / 1000, 2)

	let travelTotalCo2Emission = 0
	if (enableTravelCo2 && travelTypeCo2) {
		if (!travelType[travelTypeCo2 as keyof typeof travelType]) {
			console.error(
				'travelType not found in co2RateArray, travelTypeCo2: ',
				travelTypeCo2
			)
		}
		travelTotalCo2Emission =
			numberOfPeopleOnSetCo2 *
			percentageOfCrewTravelCo2 *
			travelType[travelTypeCo2 as keyof typeof travelType] *
			2
		travelTotalCo2Emission = round(travelTotalCo2Emission / 1000, 2)
	}
	let hotelTotalCo2Emission = 0
	if (enableTravelCo2 && percentageOfCrewTravelCo2) {
		hotelTotalCo2Emission =
			numberOfPeopleOnSetCo2 *
			(numberOfPrepDaysCo2 + numberOfShootDaysCo2) *
			percentageOfCrewTravelCo2 *
			7
		hotelTotalCo2Emission = round(hotelTotalCo2Emission / 1000, 2)
	}

	//Location 2 Travel
	let location2TravelTotalCo2Emission = 0

	if (quoteDetails.allowInternational && enableTravelCo2) {
		if (!travelType[travelTypeLocation2Co2 as keyof typeof travelType]) {
			console.error(
				'travelType not found in co2RateArray, travelTypeLocation2Co2: ',
				travelTypeLocation2Co2
			)
		}
		location2TravelTotalCo2Emission =
			numberOfPeopleOnSetCo2 *
			percentageOfCrewTravelLocation2Co2 *
			travelType[travelTypeLocation2Co2 as keyof typeof travelType] *
			2
		location2TravelTotalCo2Emission = round(
			location2TravelTotalCo2Emission / 1000,
			4
		)
	}

	return {
		mealTotalCo2Emission,
		travelTotalCo2Emission,
		hotelTotalCo2Emission,
		location2TravelTotalCo2Emission,
	}
}

export { quoteTotalsSummary }
